import { deleteCookie, getCookie, setCookie } from 'cookies-next'
import type { CookieValueTypes } from 'cookies-next'
import type { OptionsType } from 'cookies-next/lib/types'
import type { NextApiRequestCookies } from 'next/dist/server/api-utils'
import type { IncomingMessage, ServerResponse } from 'node:http'
import type { ParsedUrlQuery } from 'node:querystring'
import { z } from 'zod'

import { APP_ENV, DOMAIN } from '@/config/env'
import { reportErrorMessage } from '@/services/errorReporting'
import { UTM_KEYS } from '@/utils/url'
import type { OpprefTrackingParams } from '@flows-ui/utils/partnerTracking'
import {
  OPPREF_URL_PARAM_NAMES,
  buildOpprefTrackingParams,
  readOpprefFromSearchParams,
} from '@flows-ui/utils/partnerTracking'
import { cookieNames } from '@helpers/cookies/utils'

type CookieValues = {
  referrerName?: CookieValueTypes | string[]
  referralCode?: CookieValueTypes | string[]
  campaign?: CookieValueTypes | string[]
  coupon?: CookieValueTypes | string[]
  fa_tracking_parameter?: CookieValueTypes | string[]
  aw_tracking_parameter?: CookieValueTypes | string[]
  utm_source?: CookieValueTypes | string[]
  utm_medium?: CookieValueTypes | string[]
  utm_campaign?: CookieValueTypes | string[]
  utm_content?: CookieValueTypes | string[]
  utm_term?: CookieValueTypes | string[]
  fbclid?: CookieValueTypes | string[]
  // Partner attribution params: persisted from URL → cookie so they survive
  // navigation from the landing page into the buying flow.
  clickid?: CookieValueTypes | string[]
  gclid?: CookieValueTypes | string[]
  advertiserId?: CookieValueTypes | string[]
  channelId?: CookieValueTypes | string[]
  oppref?: CookieValueTypes | string[]
  ir_clickid?: CookieValueTypes | string[]
}

const URL_PARAM_NAMES = {
  referrerName: ['n'],
  referralCode: ['r'],
  campaign: ['campaign'],
  coupon: ['coupon'],
  fa_tracking_parameter: ['s_id'],
  aw_tracking_parameter: ['awc'],
  utm_source: ['utm_source'],
  utm_medium: ['utm_medium'],
  utm_campaign: ['utm_campaign'],
  utm_term: ['utm_term'],
  utm_content: ['utm_content'],
  fbclid: ['fbclid'],
  // Partners use either casing for the click identifier; unify to lowercase.
  clickid: ['clickId', 'clickid'],
  gclid: ['gclid'],
  advertiserId: ['advertiserId'],
  channelId: ['channelId'],
  oppref: OPPREF_URL_PARAM_NAMES,
  ir_clickid: ['irclickid'],
} as const satisfies Record<keyof CookieValues, readonly string[]>

const PERSONAL_URL_PARAM_KEYS: readonly string[] = ['referrerName']

export const FORWARDABLE_URL_PARAM_NAMES = Object.entries(URL_PARAM_NAMES)
  .filter(([key]) => !PERSONAL_URL_PARAM_KEYS.includes(key))
  .flatMap(([, names]) => names)

export const readUrlParams = (query: URLSearchParams): CookieValues => {
  // elements are null if not found, therefore we use undefined instead to set cookies
  const firstMatch = (names: readonly string[]) =>
    names.reduce<string | undefined>(
      (found, name) => found ?? query.get(name) ?? undefined,
      undefined
    )

  const params = Object.fromEntries(
    Object.entries(URL_PARAM_NAMES).map(([key, names]) => [key, firstMatch(names)])
  ) as CookieValues

  return {
    ...params,
    referralCode:
      query.get('utm_medium') === 'refer_a_friend'
        ? query.get('utm_source') ?? undefined
        : params.referralCode,
    oppref: readOpprefFromSearchParams(query),
  }
}

const EXPIRATION_DAYS = 30
const EXPIRATION_TIME_SECONDS = EXPIRATION_DAYS * 24 * 60 * 60

// https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc/
const FACEBOOK_COOKIE_MAX_AGE = 90 * 24 * 60 * 60

// Setting the domain ("hellogetsafe.com", without the subdomain "www")
// enables cookies to be read from other sub domains as well (like start.hellogetsafe.com)
const storedCookieScope = (): OptionsType =>
  DOMAIN.includes('localhost')
    ? { path: '/' }
    : { path: '/', domain: APP_ENV === 'production' ? 'hellogetsafe.com' : 'gtsf.insure' }

export const saveCookie = (
  name: string,
  value: CookieValueTypes | string[],
  options?: OptionsType
) => {
  const preparedOptions: OptionsType = {
    ...options,
    ...storedCookieScope(),
    maxAge: options?.maxAge ?? EXPIRATION_TIME_SECONDS,
  }

  // Don't apply the following options for localhost for cookies to work there.
  if (!DOMAIN.includes('localhost')) {
    preparedOptions.httpOnly = options?.httpOnly
    preparedOptions.secure = options?.secure
  }

  setCookie(name, value, preparedOptions)
}

export const clearCookie = (name: string, options?: OptionsType) => {
  deleteCookie(name, { ...options, ...storedCookieScope() })
}

const readCookies = (): CookieValues => {
  return {
    referrerName: getCookie(cookieNames.referrerName),
    referralCode: getCookie(cookieNames.referralCode),
    campaign: getCookie(cookieNames.campaign),
    coupon: getCookie(cookieNames.coupon),
    fa_tracking_parameter: getCookie(cookieNames.fa_tracking_parameter),

    // Note that this cookie is being set with the "secure" flag (as required by Awin). This means
    // that it won't work when the website is run without https
    aw_tracking_parameter: getCookie(cookieNames.aw_tracking_parameter),
    utm_source: getCookie(cookieNames.utm_source),
    utm_medium: getCookie(cookieNames.utm_medium),
    utm_campaign: getCookie(cookieNames.utm_campaign),
    utm_term: getCookie(cookieNames.utm_term),
    utm_content: getCookie(cookieNames.utm_content),
    clickid: getCookie(cookieNames.clickid),
    gclid: getCookie(cookieNames.gclid),
    advertiserId: getCookie(cookieNames.advertiserId),
    channelId: getCookie(cookieNames.channelId),
    oppref: getCookie(cookieNames.oppref),
    ir_clickid: getCookie(cookieNames.ir_clickid),
  }
}

const returnCookieValues = (parsedCookies: CookieValues, urlParams: CookieValues) => ({
  referrerName: parsedCookies.referrerName || urlParams.referrerName,
  referralCode: parsedCookies.referralCode || urlParams.referralCode,
  campaign: parsedCookies.campaign || urlParams.campaign,
  coupon: parsedCookies.coupon || urlParams.coupon,
  fa_tracking_parameter: parsedCookies.fa_tracking_parameter || urlParams.fa_tracking_parameter,
  aw_tracking_parameter: parsedCookies.aw_tracking_parameter || urlParams.aw_tracking_parameter,
  utm_source: parsedCookies.utm_source || urlParams.utm_source,
  utm_medium: parsedCookies.utm_medium || urlParams.utm_medium,
  utm_campaign: parsedCookies.utm_campaign || urlParams.utm_campaign,
  utm_term: parsedCookies.utm_term || urlParams.utm_term,
  utm_content: parsedCookies.utm_content || urlParams.utm_content,
  fbclid: parsedCookies.fbclid || urlParams.fbclid,
  clickid: parsedCookies.clickid || urlParams.clickid,
  gclid: parsedCookies.gclid || urlParams.gclid,
  advertiserId: parsedCookies.advertiserId || urlParams.advertiserId,
  channelId: parsedCookies.channelId || urlParams.channelId,
  oppref: parsedCookies.oppref || urlParams.oppref,
  ir_clickid: parsedCookies.ir_clickid || urlParams.ir_clickid,
})

export const getFromCookiesOrUrlParams = (query: URLSearchParams) => {
  const urlParams = readUrlParams(query)
  const parsedCookies = readCookies()

  return returnCookieValues(parsedCookies, urlParams)
}

const hasUtmParameters = ({
  utm_source,
  utm_medium,
  utm_campaign,
  utm_content,
  utm_term,
}: CookieValues) => {
  return Boolean(utm_source || utm_medium || utm_campaign || utm_term || utm_content)
}

const removeUtmParametersFromCookies = () => {
  UTM_KEYS.forEach((key) => clearCookie(cookieNames[key]))
}

type CookieWrite = { name: string; value: CookieValueTypes | string[] | undefined | null }

const sanitizeReferralCode = (referralCode: CookieValueTypes | string[]) => {
  const sanitizedCode = String(referralCode).slice(0, 9)
  if (referralCode === sanitizedCode) {
    return sanitizedCode
  }

  reportErrorMessage(`There are invalid characters in the referral code: ${referralCode}`)
  return undefined
}

/**
 * A referral is mutually exclusive with a coupon/campaign, so each visit
 * resolves the whole group at once: `null` clears the cookie, `undefined`
 * leaves it untouched.
 */
const resolveExclusiveCookies = (urlParams: CookieValues): CookieWrite[] => {
  if (urlParams.coupon || urlParams.campaign) {
    const supersedesStoredCoupon =
      Boolean(urlParams.coupon) && urlParams.coupon !== getCookie(cookieNames.coupon)

    return [
      { name: cookieNames.referrerName, value: null },
      { name: cookieNames.referralCode, value: null },
      { name: cookieNames.campaign, value: urlParams.campaign },
      { name: cookieNames.coupon, value: urlParams.coupon },
      ...(supersedesStoredCoupon ? [{ name: cookieNames.couponAmount, value: null }] : []),
    ]
  }

  if (urlParams.referralCode) {
    return [
      { name: cookieNames.referrerName, value: urlParams.referrerName },
      { name: cookieNames.referralCode, value: sanitizeReferralCode(urlParams.referralCode) },
      { name: cookieNames.campaign, value: null },
      { name: cookieNames.coupon, value: null },
      { name: cookieNames.couponAmount, value: null },
    ]
  }

  return [
    { name: cookieNames.referrerName, value: urlParams.referrerName },
    { name: cookieNames.campaign, value: urlParams.campaign },
    { name: cookieNames.coupon, value: urlParams.coupon },
  ]
}

const saveUrlParamToCookies = (urlParams: CookieValues) => {
  const cookiesToSave: CookieWrite[] = [
    ...resolveExclusiveCookies(urlParams),
    { name: cookieNames.fa_tracking_parameter, value: urlParams.fa_tracking_parameter },
    { name: cookieNames.aw_tracking_parameter, value: urlParams.aw_tracking_parameter },
    { name: cookieNames.utm_source, value: urlParams.utm_source },
    { name: cookieNames.utm_medium, value: urlParams.utm_medium },
    { name: cookieNames.utm_campaign, value: urlParams.utm_campaign },
    { name: cookieNames.utm_term, value: urlParams.utm_term },
    { name: cookieNames.utm_content, value: urlParams.utm_content },
    { name: cookieNames.clickid, value: urlParams.clickid },
    { name: cookieNames.gclid, value: urlParams.gclid },
    { name: cookieNames.advertiserId, value: urlParams.advertiserId },
    { name: cookieNames.channelId, value: urlParams.channelId },
    { name: cookieNames.oppref, value: urlParams.oppref },
    { name: cookieNames.ir_clickid, value: urlParams.ir_clickid },
  ]

  cookiesToSave.forEach(({ name, value }) => {
    if (value === null) {
      clearCookie(name)
      return
    }
    // Skip undefined: cookies-next serializes undefined as '', which would
    // blank out cookies that were set elsewhere (e.g. a validated coupon
    // applied inside the buying flow) on every page load via ConsentManager.
    if (value === undefined) {
      return
    }
    saveCookie(name, value)
  })
}

export const queryToSearchParams = (query: ParsedUrlQuery) => {
  const searchParams = new URLSearchParams()
  Object.entries(query).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, Array.isArray(value) ? value.join(',') : value)
    }
  })
  return searchParams
}

type RequestWithCookies = IncomingMessage & { cookies: NextApiRequestCookies }

export const getFacebookParams = (serverParams?: {
  req?: RequestWithCookies
  res?: ServerResponse<IncomingMessage>
}): { fbc?: CookieValueTypes; fbp?: CookieValueTypes } => {
  const params = serverParams?.req && serverParams?.res ? serverParams : undefined

  const fbc = getCookie('_fbc', params)
  const fbp = getCookie('_fbp', params)

  return {
    ...(fbc ? { fbc } : {}),
    ...(fbp ? { fbp } : {}),
  }
}

export const getOpprefParams = (serverParams?: {
  req?: RequestWithCookies
  res?: ServerResponse<IncomingMessage>
}): OpprefTrackingParams => {
  const params = serverParams?.req && serverParams?.res ? serverParams : undefined

  return buildOpprefTrackingParams(getCookie(cookieNames.oppref, params))
}

const isFbcForClickId = (fbc: CookieValueTypes, fbclid: NonNullable<CookieValues['fbclid']>) =>
  typeof fbc === 'string' && fbc.endsWith(`.${String(fbclid)}`)

export const setFacebookCookies = (
  cookieValues: CookieValues,
  serverParams?: {
    req?: RequestWithCookies
    res?: ServerResponse<IncomingMessage>
  }
) => {
  const fbclid = cookieValues.fbclid

  if (!fbclid) {
    return
  }

  const cookieOptions: OptionsType = { ...serverParams, maxAge: FACEBOOK_COOKIE_MAX_AGE }
  const timestamp = Date.now()

  const existingFbc = getCookie('_fbc', serverParams)

  // https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc/#2--format-clickid
  if (!isFbcForClickId(existingFbc, fbclid)) {
    saveCookie('_fbc', `fb.1.${timestamp}.${fbclid}`, cookieOptions)
  }

  if (!getCookie('_fbp', serverParams)) {
    const uniqueId = getCookie(cookieNames.browser_id, serverParams)

    // https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc/#fbp
    saveCookie('_fbp', `fb.1.${timestamp}.${uniqueId}`, cookieOptions)
  }
}

/*
This function
1. reads parameters from the URL
2. reads parameters from cookies
3. saves parameters from URL to cookies
4. returns values set either in the URL or in cookies
 */
export const processCookies = (query: ParsedUrlQuery): CookieValues => {
  const urlParams = readUrlParams(queryToSearchParams(query))
  if (hasUtmParameters(urlParams)) {
    removeUtmParametersFromCookies()
  }
  saveUrlParamToCookies(urlParams)
  setFacebookCookies(urlParams)

  try {
    const parsedCookies = readCookies()
    // It looks like we could just return the `cookies` variable, since it looks like it contains both
    // URL parameters and cookies at this point (setting cookies is a sync operation). But on the
    // server setting a cookie is done on the response object, while reading a cookie is done on the
    // request. Therefore it is not possible to read a cookie that was just set.

    return returnCookieValues(parsedCookies, urlParams)
  } catch {
    return urlParams
  }
}

export const checkCookieFirstConsent = (params: {
  keys: string[]
  req: RequestWithCookies
  res: ServerResponse<IncomingMessage>
}) => {
  const { keys, req, res } = params

  const cookieFirst = getCookie('cookiefirst-consent', { req, res })

  const schema = z.string().transform((value) => {
    try {
      return JSON.parse(value)
    } catch {
      return {}
    }
  })

  const parsedCookie = schema.safeParse(cookieFirst)

  return parsedCookie.success ? keys.every((key) => parsedCookie.data[key] === true) : false
}

export const getMarketingConsent = (serverParams: {
  req: RequestWithCookies
  res: ServerResponse<IncomingMessage>
}) => {
  const consent = getCookie('cookiefirst-consent', serverParams)
  return consent ?? false
}
